import {
  Cog,
  Hash,
  LetterText,
  LucideIcon,
  Palette,
  Pencil,
  ToggleRight,
  X,
} from "lucide-react"
import { SeamlessInput } from "./SeamlessInput"
import { CSSProperties, ReactNode, useCallback, useState } from "react"
import { ColorPickerButton } from "./ColorPickerButton"
import { Color, Expr } from "../../shared/Expr"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { NumberPickerButton } from "./NumberPickerButton"
import Tooltip from "@mui/material/Tooltip"
import { styled, TextField } from "@mui/material"

type ColorProps = {
  defaultValue: Color
  onDefaultValueChange: (paramId: string, newValue: string) => void
}
type NumberProps = {
  defaultValue: number
  onDefaultValueChange: (paramId: string, newValue: number) => void
}
type BooleanProps = {
  defaultValue: boolean
  onDefaultValueChange: (paramId: string, newValue: boolean) => void
}
type ParameterDefinitionChipBaseProps = {
  id: string
  name: string
  onEdit?: (paramId: string) => void
  onDelete: (paramId: string) => void
  onNameChange: (paramId: string, newName: string) => void
}
export function ParameterDefinitionChipColor({
  id,
  name,
  defaultValue,
  onDefaultValueChange,
  onNameChange,
  onEdit,
  onDelete,
}: ParameterDefinitionChipBaseProps & Omit<ColorProps, "type">) {
  return (
    <ParameterDefinitionChip2
      id={id}
      name={name}
      icon={Palette}
      onNameChange={onNameChange}
      onEdit={onEdit}
      onDelete={onDelete}
    >
      <Tooltip title={`Default: ${defaultValue}`}>
        <ColorPickerButton
          noParams
          color={Expr.lit(defaultValue)}
          onChange={(color) =>
            onDefaultValueChange(id, Expr.evaluate(color, {}))
          }
        >
          {(clickHandler, ref) => (
            <button
              ref={ref}
              onClick={clickHandler}
              style={{ width: 30, background: defaultValue, borderRadius: 2 }}
            ></button>
          )}
        </ColorPickerButton>
      </Tooltip>
    </ParameterDefinitionChip2>
  )
}

export function ParameterDefinitionChipNumber({
  id,
  name,
  defaultValue,
  onDefaultValueChange,
  onNameChange,
  onEdit,
  onDelete,
}: ParameterDefinitionChipBaseProps & NumberProps) {
  return (
    <ParameterDefinitionChip2
      id={id}
      name={name}
      icon={Hash}
      onNameChange={onNameChange}
      onEdit={onEdit}
      onDelete={onDelete}
    >
      <Tooltip title={`Default: ${defaultValue}`}>
        <div style={{ flex: 1, padding: "0 4px" }}>
          <NumberPickerButton
            noParams
            number={Expr.lit(defaultValue)}
            onChange={(color) =>
              onDefaultValueChange(id, Expr.evaluate(color, {}))
            }
          >
            {(clickHandler, ref) => (
              <button
                ref={ref}
                onClick={clickHandler}
                style={{ width: 30, borderRadius: 2 }}
              >
                {defaultValue}
              </button>
            )}
          </NumberPickerButton>
        </div>
      </Tooltip>
    </ParameterDefinitionChip2>
  )
}

export function ParameterDefinitionChipBoolean({
  id,
  name,
  defaultValue,
  onNameChange,
  onEdit,
  onDelete,
  onDefaultValueChange,
}: ParameterDefinitionChipBaseProps & BooleanProps) {
  return (
    <ParameterDefinitionChip2
      id={id}
      name={name}
      icon={ToggleRight}
      onNameChange={onNameChange}
      onEdit={onEdit}
      onDelete={onDelete}
    >
      <Tooltip title={`Default: ${defaultValue ? "Yes" : "No"}`}>
        <div
          style={{ width: 30, borderRadius: 2, textAlign: "center" }}
          onClick={() => onDefaultValueChange(id, !defaultValue)}
        >
          {defaultValue ? "✔" : "✘"}
        </div>
      </Tooltip>
    </ParameterDefinitionChip2>
  )
}

export function ParameterDefinitionChipString({
  id,
  name,
  defaultValue,
  onNameChange,
  onEdit,
  onDelete,
  onDefaultValueChange,
}: ParameterDefinitionChipBaseProps & ColorProps) {
  return (
    <ParameterDefinitionChip2
      id={id}
      name={name}
      icon={LetterText}
      onNameChange={onNameChange}
      onEdit={onEdit}
      onDelete={onDelete}
    >
      <TextField
        size="small"
        slotProps={{
          input: { style: { fontSize: 12 }, disableUnderline: true },
        }}
        style={{ height: 10, fontSize: 5 }}
        fullWidth
        variant="standard"
        value={defaultValue}
        onChange={(ev) => onDefaultValueChange(id, ev.target.value)}
      />
    </ParameterDefinitionChip2>
  )
}

type ParameterDefinitionChip2Props = {
  id: string
  icon: LucideIcon
  name: string
  onEdit?: (paramId: string) => void
  onDelete: (paramId: string) => void
  onNameChange: (paramId: string, newName: string) => void
  // default value
  children: ReactNode
}

const nameStyle: CSSProperties = {
  padding: "2px 4px",
  borderRadius: 3,
  margin: "0 -4px",
}

export function ParameterDefinitionChip2({
  id,
  name,
  onDelete,
  onEdit,
  onNameChange,
  icon,
  children,
}: ParameterDefinitionChip2Props) {
  const [isEditingName, setIsEditingName] = useState(false)
  const [editingName, setEditingName] = useState(name)

  const currentPage = useCurrentPage()
  const params = currentPage.meta.parameters

  const error = Object.keys(params)
    .filter((p) => p !== id)
    .map((p) => params[p])
    .map((p) => p.name)
    .includes(editingName)

  const handleInputBlur = useCallback(function handleInputBlur(value: string) {
    setIsEditingName(false)
    if (
      Object.values(params)
        .map((p) => p.name)
        .includes(value)
    ) {
    } else {
      onNameChange(id, value)
    }
  }, [])

  const handleInputChange = useCallback(function handleInputChange(
    value: string,
  ) {
    setEditingName(value)
  }, [])

  const Icon = icon

  return (
    <ParameterChipDiv>
      <Icon size={18} />

      {isEditingName ? (
        <SeamlessInput
          style={nameStyle}
          value={name}
          onBlur={handleInputBlur}
          onInputChange={handleInputChange}
          error={error ? "Name already used" : undefined}
        />
      ) : (
        <span style={nameStyle} onClick={() => setIsEditingName(true)}>
          {name}
        </span>
      )}

      <span>=</span>

      <div
        style={{
          display: "inline-flex",
          height: 18,
          fontWeight: "bold",
          outline: "1px solid #ccc",
          background: "#eee",
          alignItems: "stretch",
          padding: 2,
          borderRadius: 4,
        }}
      >
        {children}
      </div>

      {onEdit && (
        <Pencil size={15} className="edit-icon" onClick={() => onEdit(id)} />
      )}
      <X size={18} className="delete-icon" onClick={() => onDelete(id)} />
    </ParameterChipDiv>
  )
}

export const ParameterChipDiv = styled("div")({
  display: "inline-flex",
  gap: 6,
  alignItems: "center",
  padding: "4px 8px 4px 8px",
  borderRadius: 999,
  background: "rgb(210, 230, 255)",
  border: "1px solid rgb(170, 210, 255)",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1,
  "& .delete-icon": {
    maxWidth: 0,
    transition: "max-width .3s",
    cursor: "pointer",
    opacity: 0.8,
    marginRight: -4,
  },
  "& .delete-icon:hover": {
    opacity: 1,
  },
  "&:hover .delete-icon": {
    maxWidth: 28,
  },
  "& .edit-icon": {
    maxWidth: 0,
    transition: "max-width .3s",
    cursor: "pointer",
    opacity: 0.8,
    marginRight: -4,
  },
  "& .edit-icon:hover": {
    opacity: 1,
  },
  "&:hover .edit-icon": {
    maxWidth: 28,
  },
})
